import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserService } from '../services/supabase';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  const email = searchParams.get('email') || '';
  const reason = searchParams.get('reason') || 'signup';
  const status = searchParams.get('status');
  const callbackMessage = searchParams.get('message');

  const heading = useMemo(() => {
    if (reason === 'unconfirmed') return 'Confirm your email to continue';
    return 'Check your inbox';
  }, [reason]);

  const description = useMemo(() => {
    if (reason === 'unconfirmed') {
      return 'Your account exists, but your email is not verified yet. Confirm your email before signing in.';
    }
    return 'We sent a confirmation link to your email. Open that link to verify your account.';
  }, [reason]);

  const handleResend = async () => {
    if (!email) {
      setResendError('Enter your email from the sign-up flow to resend confirmation.');
      setResendMessage(null);
      return;
    }

    setResendLoading(true);
    setResendError(null);
    setResendMessage(null);

    try {
      const userService = new UserService();
      const { error } = await userService.resendSignupConfirmation(email);
      if (error) throw error;
      setResendMessage('Verification email sent. Check your inbox and spam folder.');
    } catch (error: any) {
      setResendError(error?.message || 'Unable to resend verification email right now.');
    } finally {
      setResendLoading(false);
    }
  };

  const loginUrl = email
    ? `/auth?action=login&email=${encodeURIComponent(email)}`
    : '/auth?action=login';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="glass-card rounded-xl p-8 shadow-lg space-y-5">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold text-gradient">{heading}</h1>
            <p className="text-muted-foreground">{description}</p>
            {email && (
              <p className="text-sm text-foreground">
                Email: <span className="font-medium">{email}</span>
              </p>
            )}
          </div>

          {status === 'error' && callbackMessage && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
              {callbackMessage}
            </div>
          )}

          {resendMessage && (
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-700 dark:text-green-300 text-sm">
              {resendMessage}
            </div>
          )}

          {resendError && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
              {resendError}
            </div>
          )}

          <div className="space-y-3">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendLoading}
              className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary-dark transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendLoading ? 'Sending...' : 'Resend verification email'}
            </button>

            <button
              type="button"
              onClick={() => navigate(loginUrl)}
              className="w-full py-2 px-4 border border-input rounded-lg font-medium hover:bg-accent transition-colors"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
